import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { DeactivatesLoadingSiteGuard } from "./modules/loading-site/helper/deactivates-loading-site.guard";
import { DeactivatesProductGuard } from "./modules/product/helper/deactivates-product.guard";
import { ROLES } from "./roles";
import { AuthSessionGuard } from "./helper/auth-session.guard";
import { AndroidPaymentHandoffGuard } from "./helper/android-payment-handoff.guard";
import { IosPurchaseBlockGuard } from "./helper/ios-purchase-block.guard";
import { RoleGuard } from "./helper/role.guard";
import { PermissionGuard } from "./helper/permission.guard";
import { InventoryModuleGuard } from "./helper/inventory-module.guard";
import { OfflineGuard } from "./helper/offline.guard";
import { LoadingSiteDetailsResolver } from "./modules/extras/resolvers/loading-site-details.resolver";
import { AccountDetailsResolver } from "./modules/extras/resolvers/account-details.resolver";
import { CompanyDetailsResolver } from "./modules/extras/resolvers/company-details.resolver";
import { ProductDetailResolver } from "./modules/extras/resolvers/product-detail.resolver";
import { SubscriptionPackageDetailResolver } from "./modules/extras/resolvers/subscription-package-detail.resolver";
import { PaymentDetailResolver } from "./modules/extras/resolvers/payment-detail.resolver";
import { AppRoutePathConstants } from "./commons/app-route-path-constants";
import { LoginRedirectGuard } from "./helper/login-redirect.guard";
import { CanDeactivateGuard } from "./guards/confirm-unsaved-changes.guard";
import { TranslatePipe } from "src/app/i18n/translate.pipe";

const routes: Routes = [
  {
    // Affiliate Partner Program (Phase 3) — public, unauthenticated: reachable by someone who
    // has never logged in and has no account yet. OfflineGuard alone (added 2026-08-23, Android
    // catch-up pass) is safe here — it only ever fires inside the native app when offline, and
    // is a no-op on Web/when not logged in otherwise, matching the public Share Link routes'
    // existing precedent (see "s"/"tally-sheet/share" routes further down this file).
    path: "r/:affiliateCode",
    title: "LumberLinq",
    canActivate: [OfflineGuard],
    loadComponent: () => import("./modules/affiliate/affiliate-redirect/affiliate-redirect.component").then(m => m.AffiliateRedirectComponent),
  },
  {
    // Phase 4 — public outsider application. Reachable by someone with no account at all (the
    // request itself creates their shell login). OfflineGuard added 2026-08-23 — same reasoning
    // as the referral route above.
    path: "join-as-affiliate",
    title: "Become a LumberLinq Affiliate",
    data: {
      outsiderMode: true,
      seo: "index",
      description: "Join the LumberLinq affiliate program and refer timber traders and exporters to LumberLinq, the timber management software.",
    },
    canActivate: [OfflineGuard],
    loadComponent: () => import("./modules/affiliate/affiliate-apply/affiliate-apply.component").then(m => m.AffiliateApplyComponent),
  },
  {
    // Phase 4 — self-serve application by an already-logged-in real subscriber. Deliberately
    // no RoleGuard — a subscriber applying does not have the AFFILIATE role yet at this point,
    // that's the whole point of this route. OfflineGuard added 2026-08-23 (Android catch-up).
    path: "affiliate/apply",
    title: "Become a LumberLinq Affiliate",
    canActivate: [AuthSessionGuard, OfflineGuard],
    loadComponent: () => import("./modules/affiliate/affiliate-apply/affiliate-apply.component").then(m => m.AffiliateApplyComponent),
  },
  {
    // Phase 5 — the Affiliate Portal proper. RoleGuard(ROLE_AFFILIATE) here is what makes the
    // AuthSessionGuard shell-tenant bypass meaningful in practice: a shell-tenant-only affiliate
    // can reach these routes even with no real subscription, but nothing else in the app.
    // OfflineGuard added 2026-08-23 — this portal has no offline data caching of its own.
    path: "affiliate/dashboard",
    title: "Affiliate Dashboard",
    canActivate: [AuthSessionGuard, RoleGuard, OfflineGuard],
    data: { roles: ["ROLE_AFFILIATE"] },
    loadComponent: () => import("./modules/affiliate/affiliate-dashboard/affiliate-dashboard.component").then(m => m.AffiliateDashboardComponent),
  },
  {
    // prompt166's seeded "Affiliate Portal" sidebar menu item links here — redirect to the
    // dashboard so that link actually goes somewhere.
    path: "affiliate",
    pathMatch: "full",
    redirectTo: "affiliate/dashboard",
  },
  {
    // OfflineGuard added 2026-08-23 (Android catch-up) — no offline caching for this portal.
    path: "affiliate/payout-account",
    title: "Affiliate Payout Account",
    canActivate: [AuthSessionGuard, RoleGuard, OfflineGuard],
    data: { roles: ["ROLE_AFFILIATE"] },
    loadComponent: () => import("./modules/affiliate/affiliate-payout-account/affiliate-payout-account.component").then(m => m.AffiliatePayoutAccountComponent),
  },
  {
    // Addendum Steps 2-3 — required before Phase 6 will let an admin record a payout for this
    // affiliate (see AffiliatePayoutRecordServiceImpl.isKycComplete()). OfflineGuard added
    // 2026-08-23 (Android catch-up) — no offline caching for this portal.
    path: "affiliate/verification",
    title: "Affiliate Verification",
    canActivate: [AuthSessionGuard, RoleGuard, OfflineGuard],
    data: { roles: ["ROLE_AFFILIATE"] },
    loadComponent: () => import("./modules/affiliate/affiliate-verification/affiliate-verification.component").then(m => m.AffiliateVerificationComponent),
  },
  {
    // Sales CRM (Sales CRM Phase 0) — Rikexim's internal sales + marketing tool, NOT a customer
    // feature. RoleGuard(.some) passes for SALES or SALES_MANAGER; SUPER_ADMIN / ROOT bypass it.
    // prompt189 seeds the "Sales CRM" sidebar menu item pointing here. The server enforces the
    // same roles + the Config.salesCrmEnabled kill switch (SalesCrmAccessService) — this guard is
    // the frontend half. OfflineGuard: the CRM has no offline caching of its own (Phase 7 field
    // tools bring their own).
    path: "crm",
    title: "Sales CRM",
    canActivate: [AuthSessionGuard, RoleGuard, OfflineGuard],
    data: { roles: ["ROLE_SALES", "ROLE_SALES_MANAGER"] },
    loadComponent: () => import("./modules/crm/crm-home/crm-home.component").then(m => m.CrmHomeComponent),
  },
  {
    // Sales CRM Phase 6 — public lead-capture form. No auth, no app shell. The marketing team
    // links to app.lumberlinq.com/l/<slug>. Posts to /openforall/crm/lead/<slug>. OfflineGuard
    // only — same pattern as the public affiliate routes above.
    path: "l/:slug",
    title: "Contact LumberLinq",
    canActivate: [OfflineGuard],
    loadComponent: () => import("./modules/crm/lead-form/lead-form-page.component").then(m => m.LeadFormPageComponent),
  },
  {
    path: "tally-sheet/tally-sheet",
    title: "Tally Sheet",
    canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
    loadComponent: () => import("./modules/tally-sheet/tally-sheet-four/tally-sheet-four.component").then(m => m.TallySheetFourComponent),
  },
  {
    path: "login",
    title: "LumberLinq – Sign In to Timber Management Software",
    loadComponent: () => import("./modules/login-logout/login/login.component").then(m => m.LoginComponent),
    canActivate: [LoginRedirectGuard],
    data: {
      title: "LumberLinq – Sign In to Timber Management Software",
      seo: "index",
      description: "Sign in to LumberLinq to manage tally sheets, consignments, stock units, inventory, packing lists and reports for your timber business.",
      header: "Welcome Back!",
      subHeader: "View and manage existing buyer profiles",
      animation: "FirstPage",
    },
  },
  { path: "login/addUser", title: "Add User", canActivate: [OfflineGuard, IosPurchaseBlockGuard], loadComponent: () => import("./modules/login-logout/login-add-user-to-company/login-add-user-to-company.component").then(m => m.LoginAddUserToCompanyComponent) },
  {
    path: "consignment-field-access",
    title: "Consignment Field Access",
    loadComponent: () => import("./modules/extras/shipment-field-access/shipment-field-access.component").then(m => m.ShipmentFieldAccessComponent),
    // PermissionGuard added (2026-09-06), same fix/reasoning as settings/application below --
    // RoleGuard alone let a custom-RBAC invitee through via RoleServiceImpl's COMPANY->ADMIN
    // compatibility injection. Matches this route's own menu item (id=18, "Consignment Field
    // Access"), which already correctly requires SHIPMENT_ACCESS.
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard, OfflineGuard],
    canDeactivate: [CanDeactivateGuard],
    data: { roles: [ROLES.ROLE_ADMIN], permissions: ['SHIPMENT_ACCESS'] },
  },
  {
    path: "fields-access",
    redirectTo: "consignment-field-access",
    pathMatch: "full",
  },
  {
    path: "tallysheet-fields-access",
    title: "Stock Unit Field Access",
    loadComponent: () => import("./modules/extras/tallysheet-field-access/tallysheet-field-access.component").then(m => m.TallysheetFieldAccessComponent),
    // PermissionGuard added (2026-09-06) -- same fix as consignment-field-access above.
    // Matches this route's own menu item (id=14, "Stock Unit Field Access"), which already
    // correctly requires TALLY_ACCESS.
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard, OfflineGuard],
    data: { roles: [ROLES.ROLE_ADMIN], permissions: ['TALLY_ACCESS'] },
  },
  {
    path: "storage",
    title: "Storage",
    loadComponent: () => import("./modules/storage/storage.component").then(m => m.StorageComponent),
    canActivate: [AuthSessionGuard, OfflineGuard, PermissionGuard],
    data: { permissions: ['STORAGE_ACCESS'] },
  },
  {
    // Notification history (docs/notification-build/PLAN.md, Phase 1) — no PermissionGuard, every
    // logged-in user sees only their own notifications (server-side recipient-scoped, R9/R10).
    path: "notifications",
    title: "Notifications",
    loadComponent: () => import("./notifications/notifications-page/notifications-page.component").then(m => m.NotificationsPageComponent),
    canActivate: [AuthSessionGuard, OfflineGuard],
  },
  {
    path: "settings/rbac",
    title: "Access Control",
    loadComponent: () => import("./modules/extras/rbac-settings/rbac-settings.component").then(m => m.RbacSettingsComponent),
    canActivate: [AuthSessionGuard, OfflineGuard, RoleGuard, PermissionGuard],
    data: {
      title: "Access Control",
      header: "Access Control",
      subHeader: "Manage per-user permissions for your company",
      roles: [ROLES.ROLE_ADMIN],
      permissions: ['EXTRAS_MANAGE'],
    },
  },
  {
    path: "settings/application",
    title: "Application Settings",
    loadComponent: () => import("./modules/extras/application-settings/application-settings.component").then(m => m.ApplicationSettingsComponent),
    // PermissionGuard added (2026-09-06) alongside the existing RoleGuard, matching
    // settings/rbac's own already-correct pattern below -- RoleGuard alone let a
    // custom-RBAC invitee through here, because RoleServiceImpl.applyCompatibilityRoles()
    // injects "ADMIN" into anyone holding the "COMPANY" role (which every invited user
    // gets). PermissionGuard checks the user's REAL granted permission codes instead,
    // unaffected by that shim -- see this same incident's backend-side fix
    // (prompt217-menu-application-settings-permission-code.sql).
    canActivate: [AuthSessionGuard, OfflineGuard, RoleGuard, PermissionGuard],
    data: {
      title: "Application Settings",
      header: "Application Settings",
      subHeader: "Payment reminders and other company-wide settings",
      roles: [ROLES.ROLE_ADMIN],
      permissions: ['EXTRAS_MANAGE'],
    },
  },
  { path: "", redirectTo: "/login", pathMatch: "full" },
  {
    path: "pagenotfound",
    title: "Page Not Found",
    pathMatch: "full",
    loadComponent: () => import("./modules/commons/page-not-found/page-not-found.component").then(m => m.PageNotFoundComponent),
  },
  {
    // 15-day offline lock (OFFLINE_PLAN.md 1.7). Deliberately NO guard — AuthSessionGuard sends every
    // screen here while locked, so this one must always open and never redirect to itself.
    path: "offline-locked",
    title: "Offline Too Long",
    pathMatch: "full",
    loadComponent: () => import("./modules/commons/offline-locked/offline-locked.component").then(m => m.OfflineLockedComponent),
  },
  {
    path: "offline-unavailable",
    title: "Not Available Offline",
    pathMatch: "full",
    loadComponent: () => import("./modules/commons/not-available-offline/not-available-offline.component").then(m => m.NotAvailableOfflineComponent),
  },

  //loading-site
  // Real gap found 2026-07-27: these 6 routes only ever had RoleGuard (a coarse "logged-in company
  // user" check via ROLE_USER, which every authenticated user holds) — no PermissionGuard at all,
  // despite real LOADING_SITE_ACCESS/ADD/EDIT/DELETE and PRODUCT_ACCESS/ADD/EDIT/DELETE codes
  // already existing and being enforced server-side (LoadingSiteController/ProductController).
  // A user with none of those permissions could still navigate straight to these pages and fill
  // out a full add/edit form before the save was denied server-side — same class of bug found in
  // Business Partners, Shipments, Inventory, and new-tallysheet.
  // Old /loading-sites paths — kept as redirects so existing bookmarks/shared links still work
  // after the /site-locations rename below. NOTE: bare "/locations" is already taken by an
  // unrelated route (LocationSelectionComponent, a generic address picker — see further down
  // this file) — using it here would have silently made that page unreachable, so this uses
  // "/site-locations" instead.
  { path: "loading-sites", redirectTo: "site-locations", pathMatch: "full" },
  { path: "add/loading-sites", redirectTo: "add/site-locations", pathMatch: "full" },
  { path: "edit/loading-sites/:id", redirectTo: "edit/site-locations/:id", pathMatch: "full" },
  {
    path: "site-locations",
    title: "Locations",
    loadComponent: () => import("./modules/loading-site/loading-site-list/loading-site-list.component").then(m => m.LoadingSiteListComponent),
    data: {
      title: "Location Directory",
      header: "All Locations",
      subHeader: "View and manage existing location profiles",
      roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_REGISTRATION"],
      permissions: ['LOADING_SITE_ACCESS'],
    },
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard],

    //canActivate: [AppGuardGuard],
  }, //, canDeactivate: [AppGuardGuard] },
  {
    path: "add/site-locations",
    title: "New Location",
    loadComponent: () => import("./modules/loading-site/loading-site/loading-site.component").then(m => m.LoadingSiteComponent),
    canDeactivate: [DeactivatesLoadingSiteGuard],
    data: {
      title: "Add New Location",
      header: "Create Location",
      subHeader: "Input new location details",
      roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_REGISTRATION"],
      permissions: ['LOADING_SITE_ADD'],
    },
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard],

    //canActivate: [AppGuardGuard],
  }, //, canDeactivate: [AppGuardGuard] },
  {
    path: "edit/site-locations/:id",
    title: "Edit Location",
    loadComponent: () => import("./modules/loading-site/loading-site/loading-site.component").then(m => m.LoadingSiteComponent),
    canDeactivate: [DeactivatesLoadingSiteGuard],
    resolve: { loadingSite: LoadingSiteDetailsResolver },
    data: {
      title: "Modify Location Information",
      header: "Update Location",
      subHeader: "Edit details of existing locations",
      roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_REGISTRATION"],
      permissions: ['LOADING_SITE_EDIT'],
    },
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard],

    //canActivate: [AppGuardGuard],
  }, //, canDeactivate: [AppGuardGuard] },

  //product
  {
    path: "products",
    title: "Products",
    loadComponent: () => import("./modules/product/product-list/product-list.component").then(m => m.ProductListComponent),
    data: {
      title: "Product Catalog",
      header: "All Products",
      subHeader: "View and manage existing product details",
      roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_REGISTRATION"],
      permissions: ['PRODUCT_ACCESS'],
    },
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard],
  }, //, canDeactivate: [AppGuardGuard] },
  {
    path: "add/product",
    title: "New Product",
    loadComponent: () => import("./modules/product/product/product.component").then(m => m.ProductComponent),
    canDeactivate: [DeactivatesProductGuard],
    data: {
      title: "Add New Product",
      header: "Create Product",
      subHeader: "Input new product details",
      roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_REGISTRATION"],
      permissions: ['PRODUCT_ADD'],
    },
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard],

    //canActivate: [AppGuardGuard],
  }, //, canDeactivate: [AppGuardGuard] },
  {
    path: "edit/product/:id",
    title: "Edit Product",
    loadComponent: () => import("./modules/product/product/product.component").then(m => m.ProductComponent),
    canDeactivate: [DeactivatesProductGuard],
    resolve: { product: ProductDetailResolver },
    data: {
      title: "Modify Product Information",
      header: "Update Product",
      subHeader: "Edit details of existing products",
      roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_REGISTRATION"],
      permissions: ['PRODUCT_EDIT'],
    },
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard],

    //canActivate: [AppGuardGuard],
  }, //, canDeactivate: [AppGuardGuard] },

  {
    path: "add/user",
    redirectTo: "/manage/user",
    pathMatch: "full",
  },
  {
    path: "manage/user",
    title: "Manage Users",
    loadComponent: () => import("./modules/extras/manage-users/manage-users.component").then(m => m.ManageUsersComponent),
    data: {
      title: "User Management",
      header: "Manage User",
      subHeader: "View and administer user profiles",
      roles: [ROLES.ROLE_ADMIN],
      permissions: ['USERS_VIEW'],
    },
    canActivate: [AuthSessionGuard, OfflineGuard, RoleGuard, PermissionGuard],
  },

  { path: "error/forbidden", title: "Access Forbidden", loadComponent: () => import("./modules/extras/forbidden/forbidden.component").then(m => m.ForbiddenComponent) },
  { path: "restrictedPage", title: "Restricted Page", loadComponent: () => import("./modules/extras/restricted-page/restricted-page.component").then(m => m.RestrictedPageComponent) },
  {
    path: "register",
    title: "Update Your Profile",
    loadComponent: () => import("./modules/tally-sheet/account-setup/account-details/account-details.component").then(m => m.AccountDetailsComponent),
    resolve: { accountDetails: AccountDetailsResolver },
    data: {
      title: "Update Your Profile Information",
      header: "Edit Profile",
      subHeader: "Modify your personal user details",
      roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_REGISTRATION"],
    },
    canActivate: [RoleGuard, OfflineGuard],
  },
  {
    path: "company",
    title: "Company Profile",
    loadComponent: () => import("./modules/tally-sheet/account-setup/company-details/company-details.component").then(m => m.CompanyDetailsComponent),
    resolve: { companyDetails: CompanyDetailsResolver },
    data: {
      roles: [ROLES.ROLE_ADMIN],
      title: "Company Profile Management",
      header: "Your Company Profile",
      subHeader: "View or edit your company's profile details",
      permissions: ['EXTRAS_ACCESS'],
    },
    canActivate: [AuthSessionGuard, OfflineGuard, RoleGuard, PermissionGuard],
  },
  {
    path: "company/branding",
    title: "Company Branding",
    loadComponent: () => import("./modules/extras/company-branding/company-branding.component").then(m => m.CompanyBrandingComponent),
    data: {
      roles: [ROLES.ROLE_ADMIN, "ROLE_REGISTRATION"],
      title: "Company Branding",
      header: "Company Branding",
      subHeader: "Manage your company logo",
      permissions: ['EXTRAS_ACCESS'],
    },
    canActivate: [AuthSessionGuard, OfflineGuard, RoleGuard, PermissionGuard],
  },
  { path: "error/unusual", title: "Something Went Wrong", loadComponent: () => import("./modules/extras/unidentified-error/unidentified-error.component").then(m => m.UnidentifiedErrorComponent) },
  { path: "error/server-error", title: "Server Error", loadComponent: () => import("./modules/extras/server-error/server-error.component").then(m => m.ServerErrorComponent) },
  {
    path: "registration",
    title: "Create Your LumberLinq Account",
    canActivate: [OfflineGuard, IosPurchaseBlockGuard],
    loadComponent: () => import("./modules/extras/registration/registration.component").then(m => m.RegistrationComponent),
    children: [
      { path: "", redirectTo: "register-user", pathMatch: "full" },
      {
        path: "register-user",
        title: "Create Your LumberLinq Account",
        canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
        loadComponent: () => import("./modules/extras/user-registration/user-registration.component").then(m => m.UserRegistrationComponent),
        data: {
          roles: [ROLES.ROLE_ADMIN],
          seo: "index",
          description: "Create your LumberLinq account to run digital tally sheets, consignments, stock units and inventory for your timber business.",
          title: "Create New User Account",
          header: "Register User",
          subHeader: "Add new user details to the system",
        },
      },
      {
        path: "add-user-to-exisiting-company",
        title: "Add User to Existing Company",
        canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
        loadComponent: () => import("./modules/extras/add-user-to-existing-company/add-user-to-existing-company").then(m => m.AddUserToExistingCompanyComponent),
        data: {
          roles: [ROLES.ROLE_ADMIN],
          title: "Add New User to Existing Company",
          header: "Register User",
          subHeader: "Add new user details to exisiting company",
        },
      },

      { path: "subscription", title: "Choose Your Plan", canActivate: [AndroidPaymentHandoffGuard], loadComponent: () => import("./modules/extras/subscription/subscription.component").then(m => m.SubscriptionComponent) },
      {
        path: "company",
        title: "Company Profile",
        canActivate: [AuthSessionGuard],
        loadComponent: () => import("./modules/extras/company/company.component").then(m => m.CompanyComponent),
        data: {
          roles: [ROLES.ROLE_ADMIN],
          title: "Company Profile Management",
          header: "Your Company Profile",
          subHeader: "View or edit your company's profile details",
        },
      },
      {
        path: "register-company",
        title: "Company Profile",
        loadComponent: () => import("./modules/extras/company/company.component").then(m => m.CompanyComponent),
        data: {
          roles: [ROLES.ROLE_ADMIN],
          title: "Company Profile Management",
          header: "Your Company Profile",
          subHeader: "View or edit your company's profile details",
        },
      },
      { path: "confirmation", title: "Registration Complete", loadComponent: () => import("./modules/extras/confirmation/confirmation.component").then(m => m.ConfirmationComponent) },
    ],
  },
  {
    path: "subscriptions",
    title: "Subscription",
    resolve: { subscriptionPackage: SubscriptionPackageDetailResolver },
    // RoleGuard added (2026-09-06) — this route's own data.roles already declared ROLE_ADMIN,
    // but nothing actually enforced it, so any authenticated user (not just Admins) could view
    // and manage the company's real subscription/billing/transaction history. Confirmed live via
    // a non-admin custom-role user reaching this page directly.
    canActivate: [AuthSessionGuard, OfflineGuard, RoleGuard],
    //canActivate: [AppGuardGuard],
    data: {
      roles: [ROLES.ROLE_ADMIN],
      title: "Manage Your Subscription",
      header: "Subscription",
      subHeader: "Select or update your subscription plan",
    },
    loadComponent: () => import("./modules/subscription-view/subscription-view-main/subscription-view-main.component").then(m => m.SubscriptionViewMainComponent),
    children: [
      {
        path: "subscription-management",
        title: "Manage Subscription",
        canActivate: [OfflineGuard, AndroidPaymentHandoffGuard, IosPurchaseBlockGuard],
        loadComponent: () => import("./modules/subscription-view/subscription-management/subscription-management.component").then(m => m.SubscriptionManagementComponent),
        data: {
          title: "View Subscriptions",
          header: "View Subscription",
          subHeader: "Subheader Subscription",
        },
      },
      {
        path: "subscription-opted",
        title: "Current Subscription",
        canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
        loadComponent: () => import("./modules/subscription-view/subscription-opted/subscription-opted.component").then(m => m.SubscriptionOptedComponent),
        data: {
          title: "View Subscriptions",
          header: "View Subscription",
          subHeader: "Subheader Subscription",
        },
      },
      {
        path: "subscription-features",
        title: "Subscription Features",
        canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
        loadComponent: () => import("./modules/subscription-view/subscription-opted-features/subscription-opted-features.component").then(m => m.SubscriptionOptedFeaturesComponent),
        data: {
          title: "View Features",
          header: "View Features",
          subHeader: "Subheader Features",
        },
      },
      {
        path: "subscription-history",
        title: "Transaction History",
        canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
        loadComponent: () => import("./modules/subscription-view/transaction-history/transaction-history.component").then(m => m.TransactionHistoryComponent),
        data: {
          title: "View Transactions",
          header: "View Transactions",
          subHeader: "Subheader Transactions",
        },
      },
    ],
  },
  { path: "error/notapproved", title: "Account Pending Approval", loadComponent: () => import("./modules/extras/user-not-approved/user-not-approved.component").then(m => m.UserNotApprovedComponent) },
  { path: "error/alreadyLogedIn", title: "Already Logged In", loadComponent: () => import("./modules/extras/user-already-login/user-already-login.component").then(m => m.UserAlreadyLoginComponent) },

  {
    // Video call guest page: no login (the link's random token is the key)
    path: "public/live/:token",
    title: "Video call",
    loadComponent: () => import("./modules/crm/live-call/live-call-guest/live-call-guest.component").then(m => m.LiveCallGuestComponent),
  },
  {
    path: "public/view/sawn-timber/:containerUUID",
    loadComponent: () => import("./modules/sawn-timber/components/sawn-timber-view/sawn-timber-view-main/sawn-timber-view-main.component").then(m => m.SawnTimberViewMainComponent),
  },
  {
    path: "utility/unit-conversion",
    title: "Unit Conversion",
    canActivate: [AuthSessionGuard],
    //canActivate: [AppGuardGuard],
    loadComponent: () => import("./modules/utility/components/unit-conversion/unit-conversion.component").then(m => m.UnitConversionComponent),
    data: {
      title: "Unit Conversion Tool",
      header: "Convert Units",
      subHeader: "Enter your measurements and select units for conversion",
    },
  },
  {
    path: "utility/volume-estimates",
    title: "Cost Estimate",
    canActivate: [AuthSessionGuard],
    //canActivate: [AppGuardGuard],
    loadComponent: () => import("./modules/utility/components/volume-estimate/volume-estimate.component").then(m => m.VolumeEstimateComponent),
    data: {
      title: "Cost Estimation Tool",
      header: "Estimate Your Costs",
      subHeader: "Input your parameters and calculate the projected cost",
    },
  },
  {
    path: "utility/slab-generator",
    title: "Slab Price Generator",
    canActivate: [AuthSessionGuard],
    //canActivate: [AppGuardGuard],
    loadComponent: () => import("./modules/utility/components/slab-generator/slab-generator.component").then(m => m.SlabGeneratorComponent),
    data: {
      title: "Slab Pricing Generator",
      header: "Generate Slab Prices",
      subHeader:
        "Input slab dimensions to generate price estimations per size difference",
    },
  },
  {
    path: "utility/calculator",
    title: "Calculator",
    canActivate: [AuthSessionGuard],
    loadComponent: () => import("./modules/utility/components/calculator/calculator.component").then(m => m.UtilityCalculatorComponent),
    data: {
      title: "Calculator",
      header: "Calculator",
      subHeader: "Quick calculations, right when you need them",
    },
  },
  {
    path: "payment/success",
    title: "Payment Successful",
    canActivate: [OfflineGuard, IosPurchaseBlockGuard],
    loadComponent: () => import("./modules/extras/payment-redirection/payment-sucess/payment-sucess.component").then(m => m.PaymentSucessComponent),
  },
  {
    path: "payment/pending",
    title: "Payment Pending",
    canActivate: [OfflineGuard, IosPurchaseBlockGuard],
    loadComponent: () => import("./modules/extras/payment-redirection/payment-pending/payment-pending.component").then(m => m.PaymentPendingComponent),
  },
  {
    path: "user-added-to-existing-company/success",
    title: "User Added",
    canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
    loadComponent: () => import("./modules/extras/user-added-to-existing-company-successfully-message/user-added-to-existing-company-successfully-message.component").then(m => m.UserAddedToExistingCompanySuccessfullyMessageComponent),
  },
  {
    path: "payment/failure",
    title: "Payment Failed",
    canActivate: [OfflineGuard, IosPurchaseBlockGuard],
    loadComponent: () => import("./modules/extras/payment-redirection/payment-failure/payment-failure.component").then(m => m.PaymentFailureComponent),
  },
  { path: "locations", title: "Select Location", loadComponent: () => import("./modules/commons/location-selection/location-selection.component").then(m => m.LocationSelectionComponent) },

  {
    path: "account-setup",
    title: "Account Setup",
    canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
    // IosPurchaseBlockGuard deliberately NOT here (moved off this parent, prompt185) — it used
    // to block the WHOLE wizard, including account-details/company-details, which involve no
    // payment at all and dead-ended every new iOS signup right after email verification. It now
    // sits only on the two children below that actually matter (subscription/payment), since a
    // parent route's canActivate only runs once on first entry and can't safely be made
    // conditional on wizard progress. Android/Web are unaffected either way — this guard has
    // always been a no-op for them.
    loadComponent: () => import("./modules/tally-sheet/account-setup/account-setup/account-setup.component").then(m => m.AccountSetupComponent),
    children: [
      { path: "", redirectTo: "account-details", pathMatch: "full" }, // Redirect to account-details when account-setup is entered
      {
        path: "account-details",
        title: "Account Details",
        // AuthSessionGuard added (2026-09-06) — this is the wizard's entry step, reached directly
        // from email links (verification "Complete Setup", welcome email) that open in a fresh
        // browser with no session. With no guard here, an unauthenticated hit fell straight
        // through to AccountDetailsResolver, which then mislabeled the resulting 401 as a server
        // crash (see the resolver's own fix, same date) instead of ever reaching a login prompt.
        // Sibling step company-details already had this guard; this entry point was missed.
        canActivate: [AuthSessionGuard],
        loadComponent: () =>
          import("./modules/tally-sheet/account-setup/account-details/account-details.component").then(
            (m) => m.AccountDetailsComponent,
          ),
        resolve: { accountDetails: AccountDetailsResolver },
      },
      {
        path: "company-details",
        title: "Company Details",
        // AuthSessionGuard added (2026-09-06) so an invited user (joining an EXISTING company)
        // can never land here, however they get pointed at this route — see the invited-user
        // block at the top of AuthSessionGuard.checkOnboardingAndSubscription().
        canActivate: [AuthSessionGuard],
        loadComponent: () =>
          import("./modules/tally-sheet/account-setup/company-details/company-details.component").then(
            (m) => m.CompanyDetailsComponent,
          ),
        resolve: { companyDetails: CompanyDetailsResolver },
      },
      {
        path: "all-set",
        title: "You're All Set",
        // iOS-only confirmation screen shown after the free-forever plan auto-activates —
        // deliberately unguarded (not IosPurchaseBlockGuard) since it's the iOS-only landing
        // point, not a payment step. See ConfirmAccountSetupComponent for the Android/Web
        // equivalent this is modeled on.
        loadComponent: () =>
          import("./modules/tally-sheet/account-setup/ios-all-set/ios-all-set.component").then(
            (m) => m.IosAllSetComponent,
          ),
      },
      {
        path: "subscription",
        title: "Choose Plan",
        // AuthSessionGuard added (2026-09-06) — same invited-user block as company-details above.
        canActivate: [AuthSessionGuard, IosPurchaseBlockGuard],
        loadComponent: () =>
          import("./modules/tally-sheet/account-setup/subscription-package/subscription-package.component").then(
            (m) => m.SubscriptionPackageComponent,
          ),
        resolve: { subscriptionPackage: SubscriptionPackageDetailResolver },
      },
      {
        path: "payment",
        title: "Payment",
        loadComponent: () =>
          import("./modules/tally-sheet/account-setup/payment/payment.component").then(
            (m) => m.PaymentComponent,
          ),
        resolve: { payment: PaymentDetailResolver },
        // AuthSessionGuard added (2026-09-06) — same invited-user block as company-details above.
        canActivate: [AuthSessionGuard, AndroidPaymentHandoffGuard, IosPurchaseBlockGuard],
        canDeactivate: [CanDeactivateGuard],
      },
    ],
  },
  {
    path: "payment-confirmation",
    title: "Payment Confirmed",
    canActivate: [IosPurchaseBlockGuard],
    loadComponent: () =>
      import("./modules/tally-sheet/account-setup/confirm-account-setup/confirm-account-setup.component").then(
        (m) => m.ConfirmAccountSetupComponent,
      ),
  },
  {
    path: "payment-error",
    title: "Payment Error",
    canActivate: [IosPurchaseBlockGuard],
    loadComponent: () =>
      import("./modules/tally-sheet/account-setup/payment/payment-error/payment-error.component").then(
        (m) => m.PaymentErrorComponent,
      ),
  },
  {
    path: "plan-management-unavailable",
    title: "Plan Management",
    canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
    loadComponent: () =>
      import("./modules/extras/plan-management-unavailable/plan-management-unavailable.component").then(
        (m) => m.PlanManagementUnavailableComponent,
      ),
  },
  {
    path: "edit/account-details",
    title: "Edit Account Details",
    loadComponent: () => import("./modules/tally-sheet/account-setup/account-details/account-details.component").then(m => m.AccountDetailsComponent),
    resolve: { accountDetails: AccountDetailsResolver },
    // No RoleGuard here — this route has no data.roles configured, and RoleGuard's
    // requiredRoles.some(...) throws on an undefined array, which its own catch block would
    // then treat as a failure and redirect to /unauthorized even online. OfflineGuard alone
    // preserves today's access scope and just adds the offline redirect.
    canActivate: [OfflineGuard],
  },
  {
    path: "profile",
    title: "Your Profile",
    loadComponent: () => import("./modules/tally-sheet/account-setup/account-details/account-details.component").then(m => m.AccountDetailsComponent),
    resolve: { accountDetails: AccountDetailsResolver },
    canActivate: [AuthSessionGuard, OfflineGuard],
  },
  {
    path: AppRoutePathConstants.PATH_USER_PROFILE,
    title: "Your Profile",
    loadComponent: () => import("./modules/tally-sheet/account-setup/account-details/account-details.component").then(m => m.AccountDetailsComponent),
    resolve: { accountDetails: AccountDetailsResolver },
    canActivate: [OfflineGuard],
  },
  {
    path: "payment",
    title: "Payment",
    loadComponent: () =>
      import("./modules/tally-sheet/account-setup/payment/payment.component").then(
        (m) => m.PaymentComponent,
      ),
    resolve: { payment: PaymentDetailResolver },
    canActivate: [OfflineGuard, AndroidPaymentHandoffGuard, IosPurchaseBlockGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  { path: "tally-sheet/subscribe", title: "Subscribe", canActivate: [OfflineGuard, IosPurchaseBlockGuard], loadComponent: () => import("./modules/tally-sheet/subscribe/subscribe.component").then(m => m.SubscribeComponent) },
  {
    path: AppRoutePathConstants.PATH_TALLY_SHEET_CONFIRM_LOGOUT,
    title: "Confirm Device Sign-Out",
    canActivate: [OfflineGuard], // needs the internet — OFFLINE_PLAN.md 1.12
    loadComponent: () => import("./modules/tally-sheet/confirm-logout-devices/confirm-logout-devices.component").then(m => m.ConfirmLogoutDevicesComponent),
  },
  { path: "unauthorized", title: "Unauthorized", loadComponent: () => import("./components/extras/unauthorized/unauthorized.component").then(m => m.UnauthorizedComponent) },
  {
    path: "tally-sheets",
    title: "Tally Sheets",
    loadComponent: () => import("./modules/tally-sheet/view-tally-sheet/view-tally-sheet.component").then(m => m.ViewTallySheetComponent),
    canActivate: [OfflineGuard, AuthSessionGuard],
    data: {
      title: "Tallysheet Records",
      header: "All Tallysheet",
      subHeader: "View and manage existing tallysheet details",
    },
  },
  { path: "post-login", title: "Signing You In", loadComponent: () => import("./modules/extras/post-login/post-login.component").then(m => m.PostLoginComponent) },
  {
    path: AppRoutePathConstants.PATH_INVITED_ACCOUNT_DETAILS,
    title: "Accept Invitation",
    // Only joins an EXISTING company (never creates a new tenant — see InvitationService's
    // completeInvite()/completeInviteSocial()), but it's still in-app account creation while
    // logged out, so it's blocked on iOS for the same reason as the Sign Up form. Invited iOS
    // users can still finish this on the web or Android app.
    canActivate: [OfflineGuard, IosPurchaseBlockGuard],
    loadComponent: () => import("./modules/extras/invited-user/invited-user.component").then(m => m.InvitedUserComponent),
    data: {
      title: "Accept Invitation",
    },
  },
  {
    path: AppRoutePathConstants.PATH_EMAIL_VERIFICATION_PROMPT,
    title: "Verify Your Email",
    // Only reachable from the self-serve signup form, which is itself hidden on iOS — this is
    // defense-in-depth in case the route is hit directly. See IosPurchaseBlockGuard.
    canActivate: [OfflineGuard, IosPurchaseBlockGuard],
    loadComponent: () => import("./modules/extras/email-verification-prompt/email-verification-prompt.component").then(m => m.EmailVerificationPromptComponent),
  },
  {
    path: AppRoutePathConstants.PATH_EMAIL_VERIFICATION_SUCCESS,
    title: "Email Verified",
    canActivate: [OfflineGuard, IosPurchaseBlockGuard],
    loadComponent: () => import("./modules/extras/email-verification-success/email-verification-success.component").then(m => m.EmailVerificationSuccessComponent),
  },
  {
    path: AppRoutePathConstants.PATH_PASSWORD_RESET,
    title: "Reset Your Password",
    loadComponent: () => import("./modules/extras/password-reset/password-reset.component").then(m => m.PasswordResetComponent),
  },
  {
    // Public, no login — reached from the marketing broadcast email's Unsubscribe link.
    // OfflineGuard added 2026-08-23 (Android catch-up) — this is a live server action.
    path: "unsubscribe",
    title: "Unsubscribe from Emails",
    canActivate: [OfflineGuard],
    loadComponent: () => import("./modules/extras/unsubscribe/unsubscribe.component").then(m => m.UnsubscribeComponent),
  },

  {
    path: "tally-sheet/share",
    title: "Shared Tally Sheet",
    canActivate: [OfflineGuard],
    loadChildren: () =>
      import("./modules/new-tallysheet/new-tallysheet.module").then(
        (m) => m.NewTallysheetModule,
      ),
  },

  {
    path: "new-tallysheet",
    title: "Tally Sheet",
    canActivate: [AuthSessionGuard, PermissionGuard],
    // proc-output: a mill operator's own output tally, checked by ProcessingOutputGuard (2026-09-17).
    data: { permissions: ['TALLY_ACCESS'], permissionsExemptChildPaths: ['proc-output'] },
    loadChildren: () =>
      import("./modules/new-tallysheet/new-tallysheet.module").then(
        (m) => m.NewTallysheetModule,
      ),
  },
  {
    path: "new-tallysheet/view",
    title: "Tally Sheet",
    canActivate: [AuthSessionGuard, PermissionGuard],
    loadChildren: () =>
      import("./modules/new-tallysheet/new-tallysheet.module").then(
        (m) => m.NewTallysheetModule,
      ),
    data: { mode: "view", permissions: ['TALLY_ACCESS'] },
  },

  {
    path: "consignments",
    title: "Consignments",
    canActivate: [AuthSessionGuard, OfflineGuard, PermissionGuard],
    data: { permissions: ['SHIPMENT_ACCESS'] },
    loadChildren: () =>
      import("./modules/shipments/shipments/shipments.module").then(
        (m) => m.ShipmentsModule,
      ),
  },
  {
    // Legacy alias for bookmarked/emailed links using the old "shipments" path.
    // Mounted the same way as "consignments" (not a redirectTo) so deep child paths
    // like /shipments/list or /shipments/sv/:code keep resolving without a wildcard redirect.
    path: "shipments",
    title: "Consignments",
    canActivate: [AuthSessionGuard, OfflineGuard, PermissionGuard],
    data: { permissions: ['SHIPMENT_ACCESS'] },
    loadChildren: () =>
      import("./modules/shipments/shipments/shipments.module").then(
        (m) => m.ShipmentsModule,
      ),
  },
  {
    path: "s",
    title: "Shared Link",
    canActivate: [OfflineGuard],
    loadChildren: () =>
      import("./modules/shipments/shipments/shipments.module").then(
        (m) => m.ShipmentsModule,
      ),
  },

  // ── Inventory Management ────────────────────────────────────
  {
    path: "inventory",
    title: "Inventory",
    canActivate: [AuthSessionGuard, OfflineGuard, PermissionGuard, InventoryModuleGuard],
    data: { permissions: ['INVENTORY_ACCESS'] },
    loadChildren: () =>
      import("./modules/inventory/inventory.module").then(
        (m) => m.InventoryModule,
      ),
  },

  {
    path: "business-partners",
    title: "Business Partners",
    canActivate: [AuthSessionGuard, PermissionGuard],
    data: { permissions: ['BP_ACCESS'] },
    loadChildren: () =>
      import("./modules/business-partner/business-partner.module").then(
        (m) => m.BusinessPartnerModule,
      ),
  },

  /* {
    path: 'dashboard-new',
    canActivate: [AuthSessionGuard],
    loadChildren: () =>
      import('./modules/dashboard-new/dashboard-new.module')
        .then(m => m.DashboardNewModule)
  },*/

  { path: "dashboard-new", redirectTo: "/dashboard-v7", pathMatch: "full" },

  // V3, V5, V6 retired 2026-08-01: only V7 is reachable now. Source modules are kept in
  // place (never delete a prior dashboard version), just no longer independently routable —
  // matches the redirect pattern dashboard-new already used above.
  { path: "dashboard-v5", redirectTo: "/dashboard-v7", pathMatch: "full" },
  { path: "dashboard-v6", redirectTo: "/dashboard-v7", pathMatch: "full" },
  { path: "dashboard-v3", redirectTo: "/dashboard-v7", pathMatch: "full" },

  {
    path: "dashboard-v7",
    title: "Dashboard",
    canActivate: [AuthSessionGuard, PermissionGuard],
    data: { permissions: ['DASHBOARD_ACCESS'] },
    loadChildren: () =>
      import("./modules/dashboard-v7/dashboard-v7.module").then(
        (m) => m.DashboardV7Module,
      ),
  },

  {
    // Mill Command Center (plan §J Phase 6) — deliberately its own page, not a V7 rebuild,
    // gated to elevated roles only (locked decision) — RoleGuard's ROLE_ADMIN stacked with the
    // same PermissionGuard/DASHBOARD_ACCESS every other dashboard route already uses. Each of
    // the page's own sections still independently checks its own existing permission code
    // (INVENTORY_ACCESS/PROCESS_ACCESS/FINANCE_ACCESS/SHIPMENT_ACCESS) — this route gate is the
    // "who even gets to the page" layer on top of that. OfflineGuard added 2026-08-23 (Android
    // catch-up) — this page has no offline data caching of its own, unlike dashboard-v7 which
    // was deliberately left off this guard because dashboards degrade gracefully; Command Center
    // aggregates several live modules at once and was judged worth the explicit offline redirect.
    // 2026-09-29: OfflineGuard removed — the page now saves its last numbers on the phone and shows
    // them read-only offline with the stale banner (and how many phone changes are not in them yet).
    path: "command-center",
    title: "Command Center",
    canActivate: [AuthSessionGuard, RoleGuard, PermissionGuard],
    data: { roles: [ROLES.ROLE_ADMIN], permissions: ['DASHBOARD_ACCESS'] },
    loadComponent: () =>
      import("./modules/command-center/command-center.component").then(
        (m) => m.CommandCenterComponent,
      ),
  },

  {
    path: "reports",
    title: "Reports",
    canActivate: [AuthSessionGuard, OfflineGuard, PermissionGuard],
    data: { permissions: ['REPORT_ACCESS'] },
    loadChildren: () =>
      import("./modules/reports/reports.module").then((m) => m.ReportsModule),
  },

  {
    path: "tallysheet-view",
    title: "Tally Sheet View",
    canActivate: [OfflineGuard],
    loadChildren: () =>
      import("./modules/tallysheet-view/tallysheet-view.module").then(
        (m) => m.TallysheetViewModule,
      ),
  },

  {
    path: "admin",
    title: "Admin",
    canActivate: [RoleGuard, OfflineGuard],
    // ROLE_SUPPORT added 2026-08-21 — the individual admin pages (Control Center/Complaint
    // Management/Companies) and their backend endpoints already restrict Support correctly on
    // their own (menu_roles, @PreAuthorize); this guard only decides whether Support can get
    // past the front door at all.
    data: { roles: ["ROLE_SUPER_ADMIN", "ROLE_ROOT", "ROLE_SUPPORT"] },
    loadChildren: () =>
      import("./modules/admin/admin.module").then((m) => m.AdminModule),
  },
  {
    path: "support/tickets",
    title: "Support Tickets",
    canActivate: [AuthSessionGuard, OfflineGuard, PermissionGuard],
    data: { permissions: ['SUPPORT_ACCESS'] },
    loadChildren: () =>
      import("./modules/support-tickets/support-tickets.module").then(
        (m) => m.SupportTicketsModule,
      ),
  },

  {
    path: "sync-issues",
    title: "Offline Sync Issues",
    loadComponent: () => import("./modules/commons/sync-issues/sync-issues.component").then(m => m.SyncIssuesComponent),
    // Re-enabled 2026-09-29 (OFFLINE_PLAN.md 1.9; menu item back via prompt258-show-sync-issues-menu.sql,
    // shown only in the phone app — see SideBarComponent.withoutPhoneOnlyItems). The header's
    // "N not synced" chip opens it too.
    // No RoleGuard/permission — this only ever reads the current user's own local device
    // outbox (IndexedDB), never shared company data, and must work offline too (that's the
    // whole point), so it's deliberately NOT behind OfflineGuard either.
  },

  { path: "licenses", title: "Open Source Licenses", loadComponent: () => import("./modules/commons/licenses/licenses.component").then(m => m.LicensesComponent) },

  { path: "legal/:type", title: "Legal Documents", data: { seo: "index" }, loadComponent: () => import("./modules/commons/legal-doc/legal-doc.component").then(m => m.LegalDocComponent) },

  { path: "get-the-app", title: "Get the LumberLinq App for Android and iPhone", data: { seo: "index", description: "Download the LumberLinq timber management app for Android and iPhone." }, loadComponent: () => import("./modules/commons/get-the-app/get-the-app.component").then(m => m.GetTheAppComponent) },

  { path: "**", title: "Page Not Found", loadComponent: () => import("./modules/commons/page-not-found/page-not-found.component").then(m => m.PageNotFoundComponent) },
];

@NgModule({
  imports: [TranslatePipe, RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
export { routes }; // Export the routes array
